import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import type {
  CreativeDecisionProjection,
  CreativeReportEnrichmentItem,
} from '../../creativeDecisions';

export const CREATIVE_REPORT_LOCALE_ALL = 'all';
export const CREATIVE_REPORT_LOCALE_UNKNOWN = 'unknown';

export interface CreativeLocaleDimensionGroup {
  key: string;
  deploymentCount: number;
  conceptCount: number;
  executionCount: number;
  localeCount: number;
}

export interface CreativeLocaleDimensionAnalysisResult {
  concepts: CreativeLocaleDimensionGroup[];
  executions: CreativeLocaleDimensionGroup[];
  locales: CreativeLocaleDimensionGroup[];
  unclassifiedConceptDeployments: number;
  unresolvedExecutionDeployments: number;
  unknownLocaleDeployments: number;
}

interface MutableDimensionGroup {
  key: string;
  deployments: Set<string>;
  concepts: Set<string>;
  executions: Set<string>;
  locales: Set<string>;
}

/**
 * Descriptive analysis over the rows already loaded by Creative Decisions.
 * It never aggregates money across contexts and never infers a concept from
 * mutable provider copy. Execution and locale identity come only from the
 * exact deployment export binding.
 */
export function buildCreativeLocaleDimensionAnalysis(
  rows: readonly CreativeDecisionProjection[],
  enrichmentByDeploymentKey: Readonly<Record<string, CreativeReportEnrichmentItem | undefined>>,
): CreativeLocaleDimensionAnalysisResult {
  const concepts = new Map<string, MutableDimensionGroup>();
  const executions = new Map<string, MutableDimensionGroup>();
  const locales = new Map<string, MutableDimensionGroup>();
  let unclassifiedConceptDeployments = 0;
  let unresolvedExecutionDeployments = 0;
  let unknownLocaleDeployments = 0;

  for (const row of rows) {
    const deploymentKey = row.deployment.deploymentKey;
    const conceptKeys = reviewedConceptKeys(row);
    const exactExport = enrichmentByDeploymentKey[deploymentKey]?.enrichment;
    const executionKey = exactExport?.manifest.renditionId;
    const locale = exactExport?.manifest.locale;

    if (conceptKeys.length === 0) unclassifiedConceptDeployments += 1;
    if (!executionKey) unresolvedExecutionDeployments += 1;
    if (!locale) unknownLocaleDeployments += 1;

    for (const conceptKey of conceptKeys) {
      addDimensionObservation(concepts, conceptKey, {
        deploymentKey,
        concepts: conceptKeys,
        executions: executionKey ? [executionKey] : [],
        locales: locale ? [locale] : [],
      });
    }
    if (executionKey) {
      addDimensionObservation(executions, executionKey, {
        deploymentKey,
        concepts: conceptKeys,
        executions: [executionKey],
        locales: locale ? [locale] : [],
      });
    }
    if (locale) {
      addDimensionObservation(locales, locale, {
        deploymentKey,
        concepts: conceptKeys,
        executions: executionKey ? [executionKey] : [],
        locales: [locale],
      });
    }
  }

  return {
    concepts: finalizeGroups(concepts),
    executions: finalizeGroups(executions),
    locales: finalizeGroups(locales),
    unclassifiedConceptDeployments,
    unresolvedExecutionDeployments,
    unknownLocaleDeployments,
  };
}

export function creativeReportLocaleOptions(
  enrichmentByDeploymentKey: Readonly<Record<string, CreativeReportEnrichmentItem | undefined>>,
  selectedLocale?: string,
): string[] {
  const locales = new Set<string>();
  for (const item of Object.values(enrichmentByDeploymentKey)) {
    const locale = item?.enrichment?.manifest.locale.trim();
    if (locale) locales.add(locale);
  }
  if (selectedLocale
    && selectedLocale !== CREATIVE_REPORT_LOCALE_ALL
    && selectedLocale !== CREATIVE_REPORT_LOCALE_UNKNOWN) {
    // A deep-linked locale must remain a valid Select value while its page or
    // enrichment batch is still loading.
    locales.add(selectedLocale);
  }
  return [...locales].sort((left, right) => left.localeCompare(right));
}

export function filterCreativeDecisionsByLocale(
  rows: readonly CreativeDecisionProjection[],
  enrichmentByDeploymentKey: Readonly<Record<string, CreativeReportEnrichmentItem | undefined>>,
  localeFilter: string,
  enrichmentLoading: boolean,
): CreativeDecisionProjection[] {
  if (localeFilter === CREATIVE_REPORT_LOCALE_ALL || enrichmentLoading) return [...rows];
  return rows.filter((row) => {
    const item = enrichmentByDeploymentKey[row.deployment.deploymentKey];
    const locale = item?.enrichment?.manifest.locale;
    if (localeFilter === CREATIVE_REPORT_LOCALE_UNKNOWN) return !locale;
    return locale?.toLocaleLowerCase() === localeFilter.toLocaleLowerCase();
  });
}

export function CreativeLocaleDimensionAnalysis({
  rows,
  enrichmentByDeploymentKey,
  loading = false,
}: {
  rows: readonly CreativeDecisionProjection[];
  enrichmentByDeploymentKey: Readonly<Record<string, CreativeReportEnrichmentItem | undefined>>;
  loading?: boolean;
}): React.ReactElement {
  const analysis = React.useMemo(
    () => buildCreativeLocaleDimensionAnalysis(rows, enrichmentByDeploymentKey),
    [enrichmentByDeploymentKey, rows],
  );

  return (
    <Box sx={{ mt: 2, border: 1, borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={0.75}
        sx={{ mb: 1.25 }}
      >
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 750 }}>
            Concept · execution · locale
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Loaded rows only. Reviewed taxonomy and exact deployed exports; descriptive, not a winner verdict.
          </Typography>
        </Box>
        {loading && <Chip size="small" variant="outlined" label="Resolving exact locales…" />}
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
        <DimensionCard
          title="Concepts"
          groups={analysis.concepts}
          empty="No reviewed concept tags on loaded deployments."
          unresolved={`${analysis.unclassifiedConceptDeployments} unclassified deployment${analysis.unclassifiedConceptDeployments === 1 ? '' : 's'}`}
          detail={(group) => `${group.deploymentCount} deployments · ${group.executionCount} executions · ${group.localeCount} locales`}
        />
        <DimensionCard
          title="Executions"
          groups={analysis.executions}
          empty="No exact Segmently rendition is bound yet."
          unresolved={`${analysis.unresolvedExecutionDeployments} provider-only or unresolved deployment${analysis.unresolvedExecutionDeployments === 1 ? '' : 's'}`}
          detail={(group) => `${group.deploymentCount} deployments · ${group.conceptCount} concepts · ${group.localeCount} locales`}
        />
        <DimensionCard
          title="Locales"
          groups={analysis.locales}
          empty="No exact deployment locale is available yet."
          unresolved={`${analysis.unknownLocaleDeployments} unknown-locale deployment${analysis.unknownLocaleDeployments === 1 ? '' : 's'}`}
          detail={(group) => `${group.deploymentCount} deployments · ${group.conceptCount} concepts · ${group.executionCount} executions`}
        />
      </Box>
    </Box>
  );
}

function DimensionCard({
  title,
  groups,
  empty,
  unresolved,
  detail,
}: {
  title: string;
  groups: readonly CreativeLocaleDimensionGroup[];
  empty: string;
  unresolved: string;
  detail: (group: CreativeLocaleDimensionGroup) => string;
}): React.ReactElement {
  return (
    <Box sx={{ minWidth: 0, p: 1.25, borderRadius: 1, bgcolor: 'action.hover' }}>
      <Typography variant="caption" color="text.secondary">{title}</Typography>
      {groups.length === 0 ? (
        <Typography variant="body2" sx={{ mt: 0.5 }}>{empty}</Typography>
      ) : (
        <Stack spacing={0.75} sx={{ mt: 0.5 }}>
          {groups.slice(0, 3).map((group) => (
            <Box key={group.key} sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap title={group.key}>
                {humanizeDimensionKey(group.key)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {detail(group)}
              </Typography>
            </Box>
          ))}
          {groups.length > 3 && (
            <Typography variant="caption" color="text.secondary">{`+${groups.length - 3} more`}</Typography>
          )}
        </Stack>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
        {unresolved}
      </Typography>
    </Box>
  );
}

function reviewedConceptKeys(row: CreativeDecisionProjection): string[] {
  return [...new Set(row.artifactSnapshots.flatMap((artifact) => (
    artifact.taxonomy?.values
      .filter((value) => value.dimension === 'concept')
      .map((value) => value.valueKey.trim())
      .filter(Boolean) ?? []
  )))];
}

function addDimensionObservation(
  groups: Map<string, MutableDimensionGroup>,
  key: string,
  observation: {
    deploymentKey: string;
    concepts: readonly string[];
    executions: readonly string[];
    locales: readonly string[];
  },
): void {
  const group = groups.get(key) ?? {
    key,
    deployments: new Set<string>(),
    concepts: new Set<string>(),
    executions: new Set<string>(),
    locales: new Set<string>(),
  };
  group.deployments.add(observation.deploymentKey);
  observation.concepts.forEach((value) => group.concepts.add(value));
  observation.executions.forEach((value) => group.executions.add(value));
  observation.locales.forEach((value) => group.locales.add(value));
  groups.set(key, group);
}

function finalizeGroups(groups: Map<string, MutableDimensionGroup>): CreativeLocaleDimensionGroup[] {
  return [...groups.values()]
    .map((group) => ({
      key: group.key,
      deploymentCount: group.deployments.size,
      conceptCount: group.concepts.size,
      executionCount: group.executions.size,
      localeCount: group.locales.size,
    }))
    .sort((left, right) => right.deploymentCount - left.deploymentCount || left.key.localeCompare(right.key));
}

function humanizeDimensionKey(value: string): string {
  return value.replaceAll('_', ' ');
}
